import { useEffect, useRef } from 'react';
import { teamSlides } from './TeamSlideshow';

const stripSlides = [
  teamSlides.find((s) => s.caption === 'Bootstour in Valencia'),
  teamSlides.find((s) => s.caption === 'Horbach Bühne'),
  teamSlides.find((s) => s.caption === 'Yacht-Tag auf dem Mittelmeer'),
  teamSlides.find((s) => s.caption === 'Dinner über Barcelona'),
  teamSlides.find((s) => s.caption === 'Strategie in den Alpen'),
  teamSlides.find((s) => s.caption === 'Helikopter-Incentive'),
  teamSlides.find((s) => s.caption === 'Teamreise Valencia'),
  teamSlides.find((s) => s.caption === 'Kart-Session'),
].filter(Boolean) as { url: string; alt: string; caption: string }[];

const SPEED = 1.0; // px per frame ~ 60px/s at 60fps
const RESUME_DELAY = 1500;

const TeamMomenteStrip = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const programmaticRef = useRef(false);
  const resumeTimer = useRef<number | null>(null);
  const dragState = useRef<{ active: boolean; startX: number; startScroll: number; moved: boolean }>({
    active: false,
    startX: 0,
    startScroll: 0,
    moved: false,
  });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf = 0;
    const tick = () => {
      const half = el.scrollWidth / 2;
      if (half > 0) {
        // Normalize position into first half so it can loop endlessly in both directions
        if (el.scrollLeft >= half - 0.5) {
          programmaticRef.current = true;
          el.scrollLeft -= half;
        } else if (el.scrollLeft < 0) {
          programmaticRef.current = true;
          el.scrollLeft += half;
        }

        if (!pausedRef.current && !reduced && !dragState.current.active) {
          programmaticRef.current = true;
          el.scrollLeft += SPEED;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const pause = () => {
      pausedRef.current = true;
      if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
    };
    const scheduleResume = () => {
      if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
      resumeTimer.current = window.setTimeout(() => {
        pausedRef.current = false;
      }, RESUME_DELAY);
    };

    const onEnter = () => pause();
    const onLeave = () => scheduleResume();
    const onScroll = () => {
      if (programmaticRef.current) {
        programmaticRef.current = false;
        return;
      }
      pause();
      scheduleResume();
    };
    const onVis = () => {
      if (document.hidden) pause();
      else scheduleResume();
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVis);
      if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Only enable drag for mouse; let touch use native scrolling
    if (e.pointerType !== 'mouse') {
      pausedRef.current = true;
      return;
    }
    dragState.current = {
      active: true,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    };
    el.setPointerCapture(e.pointerId);
    pausedRef.current = true;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el || !dragState.current.active) return;
    const dx = e.clientX - dragState.current.startX;
    if (Math.abs(dx) > 3) dragState.current.moved = true;
    el.scrollLeft = dragState.current.startScroll - dx;
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (dragState.current.active) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      dragState.current.active = false;
    }
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => {
      pausedRef.current = false;
    }, RESUME_DELAY);
  };

  const items = [...stripSlides, ...stripSlides];

  return (
    <section aria-label="Team-Momente" className="bg-background border-b border-border/60">
      <div className="container mx-auto px-6 py-6 md:py-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-primary-dark font-semibold">
            Team & Momente
          </p>
          <a
            href="#mehr-als-ein-job"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Mehr sehen
          </a>
        </div>

        <div
          ref={scrollerRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="-mx-6 px-6 overflow-x-auto cursor-grab active:cursor-grabbing select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0, black 48px, black calc(100% - 48px), transparent 100%)',
            maskImage:
              'linear-gradient(to right, transparent 0, black 48px, black calc(100% - 48px), transparent 100%)',
          }}
        >
          <div className="flex gap-3 w-max">
            {items.map((s, i) => (
              <div
                key={`${s.url}-${i}`}
                className="shrink-0 w-40 md:w-44 lg:w-48 aspect-square rounded-2xl overflow-hidden bg-muted"
              >
                <img
                  src={s.url}
                  alt={s.alt}
                  loading="lazy"
                  draggable={false}
                  className="w-full h-full object-cover pointer-events-none"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamMomenteStrip;
